import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import {
  ALL_TYPES,
  buildMultiChipLabel,
  joinTypeSelection,
  splitTypeSelection,
  TYPE_LABELS,
  TypeValue,
} from '../helpers';

import { JobDashboardStatusEnum, VisibilityStatusEnum } from '@/types/graphql';

type Props = {
  visibility: VisibilityStatusEnum[] | undefined;
  jobDashboardStatus: JobDashboardStatusEnum[] | undefined;
  onApply: (next: {
    visibility: VisibilityStatusEnum[] | undefined;
    includesNoShow: boolean;
  }) => void;
  onClear: () => void;
};

const normalize = (
  visibility: VisibilityStatusEnum[] | undefined,
  jobDashboardStatus: JobDashboardStatusEnum[] | undefined,
): Set<TypeValue> => {
  const set = joinTypeSelection(visibility, jobDashboardStatus);
  if (set.size === 0) return new Set(ALL_TYPES);
  return set;
};

const TypesPill = ({
  visibility,
  jobDashboardStatus,
  onApply,
  onClear,
}: Props) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<Set<TypeValue>>(() =>
    normalize(visibility, jobDashboardStatus),
  );

  useEffect(() => {
    if (open) setStaged(normalize(visibility, jobDashboardStatus));
  }, [open, visibility, jobDashboardStatus]);

  const allSelected = staged.size === ALL_TYPES.length;

  const toggleItem = (t: TypeValue) => {
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleAll = () => {
    setStaged(allSelected ? new Set() : new Set(ALL_TYPES));
  };

  const apply = () => {
    // Both 0 and N collapse to "no filter".
    if (staged.size === 0 || staged.size === ALL_TYPES.length) {
      onApply({ visibility: undefined, includesNoShow: false });
    } else {
      onApply(splitTypeSelection(staged));
    }
    setOpen(false);
  };

  const appliedSet = joinTypeSelection(visibility, jobDashboardStatus);
  const isActive = appliedSet.size > 0 && appliedSet.size < ALL_TYPES.length;
  const selectedLabels = ALL_TYPES.filter((t) => appliedSet.has(t)).map(
    (t) => TYPE_LABELS[t],
  );
  const { label, more } = buildMultiChipLabel(selectedLabels);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="All types"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by type"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 180 }}
      >
        <div
          className="dropdown-option"
          role="checkbox"
          aria-checked={allSelected}
          onClick={toggleAll}
        >
          <span
            className={classNames('dropdown-check', { checked: allSelected })}
          >
            {allSelected ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
          </span>
          <span>{allSelected ? 'Unselect All' : 'Select All'}</span>
        </div>
        {ALL_TYPES.map((t) => {
          const checked = staged.has(t);
          return (
            <div
              key={t}
              className="dropdown-option"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggleItem(t)}
            >
              <span className={classNames('dropdown-check', { checked })}>
                {checked ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
              </span>
              {TYPE_LABELS[t]}
            </div>
          );
        })}
      </FilterDropdown>
    </div>
  );
};

export default TypesPill;
